import React from 'react'
import { getCurrentSession, login as apiLogin, logout, SuiteApiError } from '../api/suiteApi'
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import { getUsuarioFromSession } from '../utils/permisos'

const SuiteSessionContext = React.createContext(null)

const getDevCredentials = () => {
  if (process.env.NODE_ENV !== 'development') return null
  const loginValue = process.env.REACT_APP_SUITE_DEV_LOGIN
  const password = process.env.REACT_APP_SUITE_DEV_PASSWORD

  if (!loginValue || !password) return null

  return {
    login: loginValue,
    password,
  }
}

export const SuiteSessionProvider = ({ children }) => {
  const [session, setSession] = React.useState(null)
  const [status, setStatus] = React.useState('loading')
  const [error, setError] = React.useState(null)
  const [reauthRequired, setReauthRequired] = React.useState(false)
  const [reauthBusy, setReauthBusy] = React.useState(false)
  const [reauthError, setReauthError] = React.useState('')
  const [reauthLogin, setReauthLogin] = React.useState('')
  const [reauthPassword, setReauthPassword] = React.useState('')
  const currentUser = getUsuarioFromSession(session)

  React.useEffect(() => {
    const expired = () => {
      if (status !== 'authenticated') return
      if (!reauthRequired) {
        setReauthLogin(currentUser?.username || '')
        setReauthError('')
      }
      setReauthRequired(true)
    }
    window.addEventListener('suite:session-expired', expired)
    return () => window.removeEventListener('suite:session-expired', expired)
  }, [status, currentUser?.username, reauthRequired])

  const handleReauthenticate = async (event) => {
    event.preventDefault()
    if (reauthBusy) return
    setReauthBusy(true)
    setReauthError('')
    try {
      await apiLogin({ login: reauthLogin, password: reauthPassword })
      const nextSession = await getCurrentSession()
      const nextUser = getUsuarioFromSession(nextSession)
      if (currentUser?.id && String(nextUser?.id) !== String(currentUser.id)) {
        await logout()
        throw new Error('Debes volver a entrar con el mismo usuario que estaba realizando el control.')
      }
      setSession(nextSession)
      setReauthRequired(false)
      setReauthPassword('')
    } catch (loginError) {
      setReauthError(loginError.message || 'No se ha podido recuperar la sesion.')
    } finally { setReauthBusy(false) }
  }

  const loadSession = React.useCallback(async () => {
    setStatus('loading')
    setError(null)

    try {
      const currentSession = await getCurrentSession()
      setSession(currentSession)
      setStatus('authenticated')
      return currentSession
    } catch (sessionError) {
      const devCredentials = getDevCredentials()

      if (!devCredentials || sessionError.status !== 401) {
        setSession(null)
        setError(sessionError)
        setStatus('unauthenticated')
        return null
      }

      try {
        await apiLogin(devCredentials)
        const currentSession = await getCurrentSession()
        setSession(currentSession)
        setStatus('authenticated')
        return currentSession
      } catch (loginError) {
        setSession(null)
        setError(loginError)
        setStatus('unauthenticated')
        return null
      }
    }
  }, [])

  React.useEffect(() => {
    loadSession()
  }, [loadSession])

  const handleLogin = React.useCallback(async (credentials) => {
    setStatus('loading')
    setError(null)

    try {
      await apiLogin(credentials)
      const currentSession = await getCurrentSession()
      setSession(currentSession)
      setStatus('authenticated')
      return currentSession
    } catch (loginError) {
      setSession(null)
      setError(loginError)
      setStatus('unauthenticated')
      throw loginError
    }
  }, [])

  const handleLogout = React.useCallback(async () => {
    try {
      await logout()
    } catch (logoutError) {
      if (!(logoutError instanceof SuiteApiError && logoutError.status === 401)) {
        setError(logoutError)
      }
    } finally {
      setSession(null)
      setStatus('unauthenticated')
    }
  }, [])

  const value = React.useMemo(() => ({
    error,
    login: handleLogin,
    reloadSession: loadSession,
    session,
    status,
    logout: handleLogout,
    usingFallback: false,
  }), [error, handleLogin, handleLogout, loadSession, session, status])

  return (
    <SuiteSessionContext.Provider value={value}>
      {children}
      <Dialog open={reauthRequired} maxWidth="xs" fullWidth aria-labelledby="reauth-title">
        <form onSubmit={handleReauthenticate}>
          <DialogTitle id="reauth-title">Recuperar sesion</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>Tu sesion ha caducado. Los datos del control siguen abiertos. Inicia sesion y vuelve a pulsar la accion que estabas realizando.</Typography>
            {reauthError ? <Alert severity="error" sx={{ mb: 2 }}>{reauthError}</Alert> : null}
            <TextField fullWidth margin="dense" label="Usuario" value={reauthLogin} onChange={(event) => setReauthLogin(event.target.value)} autoComplete="username" required disabled={reauthBusy} slotProps={{ input: { readOnly: Boolean(currentUser?.username) } }} />
            <TextField fullWidth margin="dense" label="Contraseña" value={reauthPassword} onChange={(event) => setReauthPassword(event.target.value)} type="password" autoComplete="current-password" required disabled={reauthBusy} />
          </DialogContent>
          <DialogActions><Button type="submit" variant="contained" disabled={reauthBusy}>{reauthBusy ? 'Conectando...' : 'Continuar'}</Button></DialogActions>
        </form>
      </Dialog>
    </SuiteSessionContext.Provider>
  )
}

export const useSuiteSession = () => {
  const context = React.useContext(SuiteSessionContext)
  if (!context) {
    throw new Error('useSuiteSession debe usarse dentro de SuiteSessionProvider')
  }
  return context
}
